/**
 * Recently Viewed Products
 *
 * Two responsibilities:
 * 1. On every PDP, save the current product ID into localStorage.
 * 2. The <recently-viewed-products> custom element fetches matching products
 *    via the Shopify search API, injects the HTML, and initialises Splide.
 *
 * Storage key: "recently-viewed-products"
 * Format: JSON array of product IDs (numbers), newest first.
 */

(function () {
  const STORAGE_KEY = 'recently-viewed-products';
  const MAX_STORED = 30; // keep more than we show, so exclusions still leave enough

  /* ------------------------------------------------------------------ */
  /*  1. Save current product ID (runs on every PDP)                    */
  /* ------------------------------------------------------------------ */
  function saveCurrentProduct() {
    const productId = window.rvProductId; // set via Liquid, see snippet below
    if (!productId) return;

    let items = [];
    try {
      items = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (_) {
      items = [];
    }

    // Remove duplicate, then prepend
    items = items.filter((id) => id !== productId);
    items.unshift(productId);

    // Cap the list
    if (items.length > MAX_STORED) items = items.slice(0, MAX_STORED);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_) {
      // Storage full or unavailable – silently fail
    }
  }

  saveCurrentProduct();

  /* ------------------------------------------------------------------ */
  /*  2. <recently-viewed-products> custom element                      */
  /* ------------------------------------------------------------------ */
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();
      this._loaded = false;
    }

    connectedCallback() {
      this._load();
    }

    /** Build the search query string from stored IDs */
    _getSearchQuery() {
      let items = [];
      try {
        items = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (_) {
        return '';
      }

      const excludeId = this.dataset.excludeId
        ? parseInt(this.dataset.excludeId, 10)
        : null;

      const max = parseInt(this.dataset.productsCount || '10', 10);

      return items
        .filter((id) => id !== excludeId)
        .slice(0, max)
        .map((id) => `id:${id}`)
        .join(' OR ');
    }

    async _load() {
      if (this._loaded) return;
      this._loaded = true;

      const query = this._getSearchQuery();
      if (!query) {
        this._hideSection();
        return;
      }

      const sectionId = this.dataset.sectionId;
      const section = this.closest('.shopify-section');
      const url = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${sectionId}`;

      try {
        const response = await fetch(url, { priority: 'low' });
        if (!response.ok) throw new Error(response.status);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshElement = doc.querySelector('recently-viewed-products');

        if (!freshElement || freshElement.childElementCount === 0) {
          this._hideSection();
          return;
        }

        // Inject the server-rendered content
        this.replaceChildren(
          ...document.importNode(freshElement, true).childNodes
        );

        // Init Splide carousel via the theme's global store
        this._initSplide();
      } catch (err) {
        console.warn('[RecentlyViewed] Failed to load:', err);
        this._hideSection();
      }
    }

    /** Initialise Splide using $store.xSplide (same pattern as collection-list) */
    _initSplide() {
      // The config is baked into a data attribute by Liquid
      const configAttr = this.dataset.splideConfig;
      if (!configAttr) return;

      try {
        const config = JSON.parse(configAttr);

        // Alpine's $store.xSplide.load expects the DOM element + config object
        // Wait a tick for Alpine to pick up the new DOM
        requestAnimationFrame(() => {
          if (
            window.Alpine &&
            window.Alpine.store &&
            window.Alpine.store('xSplide')
          ) {
            window.Alpine.store('xSplide').load(this, config);
          }
        });
      } catch (err) {
        console.warn('[RecentlyViewed] Splide init error:', err);
      }
    }

    _hideSection() {
      const section = this.closest('.shopify-section');
      if (section) section.classList.add('rv-empty');
    }
  }

  if (!window.customElements.get('recently-viewed-products')) {
    window.customElements.define(
      'recently-viewed-products',
      RecentlyViewedProducts
    );
  }
})();